import React, { useState } from 'react'
import {
	Card,
	CardHeader,
	Text,
	Button,
	Input,
	Textarea,
	Dropdown,
	Option,
	Checkbox,
	RadioGroup,
	Radio,
	Field,
	ProgressBar,
	Badge,
	tokens,
	makeStyles,
	MessageBar,
	MessageBarBody,
	MessageBarTitle
} from '@fluentui/react-components'
import {
	PersonRegular,
	// BuildingRegular,
	MailRegular,
	// CalendarRegular,
	// DocumentRegular,
	CheckmarkCircleRegular,
	// ErrorCircleRegular
} from '@fluentui/react-icons'

const useStyles = makeStyles({
	container: {
		padding: '1rem',
		gap: '1rem',
		display: 'flex',
		flexDirection: 'column'
	},
	header: {
		marginBottom: '1rem'
	},
	formSection: {
		padding: '1rem',
		marginBottom: '1rem'
	},
	fieldGroup: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
		gap: '1rem',
		marginBottom: '1rem'
	},
	progressContainer: {
		marginBottom: '1rem'
	},
	validationMessage: {
		marginTop: '0.5rem',
		fontSize: '0.875rem'
	},
	submitSection: {
		display: 'flex',
		gap: '1rem',
		alignItems: 'center',
		marginTop: '1rem'
	}
})

interface FormField {
	id: string
	label: string
	type: 'text' | 'email' | 'dropdown' | 'textarea' | 'checkbox' | 'radio' | 'date'
	required: boolean
	value: any
	options?: string[]
	validation?: (value: any) => string | null
	dependsOn?: {
		field: string
		value: any
	}
}

interface ValidationResult {
	isValid: boolean
	errors: { [key: string]: string }
}

export const DynamicForm: React.FC = () => {
	const styles = useStyles()
	const [formData, setFormData] = useState<{ [key: string]: any }>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

	const formFields: FormField[] = [
		{
			id: 'firstName',
			label: 'First Name',
			type: 'text',
			required: true,
			value: formData.firstName || '',
			validation: (value) => !value || value.length < 2 ? 'First name must be at least 2 characters' : null
		},
		{
			id: 'lastName',
			label: 'Last Name',
			type: 'text',
			required: true,
			value: formData.lastName || '',
			validation: (value) => !value || value.length < 2 ? 'Last name must be at least 2 characters' : null
		},
		{
			id: 'email',
			label: 'Email Address',
			type: 'email',
			required: true,
			value: formData.email || '',
			validation: (value) => {
				if (!value) return 'Email is required'
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
				return !emailRegex.test(value) ? 'Please enter a valid email address' : null
			}
		},
		{
			id: 'department',
			label: 'Department',
			type: 'dropdown',
			required: true,
			value: formData.department || '',
			options: ['IT', 'HR', 'Finance', 'Marketing', 'Operations'],
			validation: (value) => !value ? 'Please select a department' : null
		},
		{
			id: 'requestType',
			label: 'Request Type',
			type: 'radio',
			required: true,
			value: formData.requestType || '',
			options: ['New Equipment', 'Software License', 'Access Request', 'Other'],
			validation: (value) => !value ? 'Please select a request type' : null
		},
		{
			id: 'equipmentType',
			label: 'Equipment Type',
			type: 'dropdown',
			required: true,
			value: formData.equipmentType || '',
			options: ['Laptop', 'Desktop', 'Monitor', 'Printer', 'Mobile Device'],
			dependsOn: { field: 'requestType', value: 'New Equipment' },
			validation: (value) => formData.requestType === 'New Equipment' && !value ? 'Please select equipment type' : null
		},
		{
			id: 'softwareType',
			label: 'Software Type',
			type: 'dropdown',
			required: true,
			value: formData.softwareType || '',
			options: ['Microsoft Office', 'Adobe Creative Suite', 'Development Tools', 'Other'],
			dependsOn: { field: 'requestType', value: 'Software License' },
			validation: (value) => formData.requestType === 'Software License' && !value ? 'Please select software type' : null
		},
		{
			id: 'accessLevel',
			label: 'Access Level Required',
			type: 'dropdown',
			required: true,
			value: formData.accessLevel || '',
			options: ['Read Only', 'Read/Write', 'Admin', 'Full Control'],
			dependsOn: { field: 'requestType', value: 'Access Request' },
			validation: (value) => formData.requestType === 'Access Request' && !value ? 'Please select access level' : null
		},
		{
			id: 'urgency',
			label: 'Urgency Level',
			type: 'radio',
			required: true,
			value: formData.urgency || '',
			options: ['Low', 'Medium', 'High', 'Critical'],
			validation: (value) => !value ? 'Please select urgency level' : null
		},
		{
			id: 'businessJustification',
			label: 'Business Justification',
			type: 'textarea',
			required: true,
			value: formData.businessJustification || '',
			validation: (value) => !value || value.length < 10 ? 'Please provide at least 10 characters of justification' : null
		},
		{
			id: 'agreedToTerms',
			label: 'I agree to the terms and conditions',
			type: 'checkbox',
			required: true,
			value: formData.agreedToTerms || false,
			validation: (value) => !value ? 'You must agree to the terms and conditions' : null
		}
	]

	const visibleFields = formFields.filter(field => {
		if (!field.dependsOn) return true
		return formData[field.dependsOn.field] === field.dependsOn.value
	})

	// Validation and progress are derived from form state on every render —
	// no effect/state round-trip needed.
	const validateForm = (): ValidationResult => {
		const errors: { [key: string]: string } = {}
		let isValid = true

		visibleFields.forEach(field => {
			if (field.validation) {
				const error = field.validation(formData[field.id])
				if (error) {
					errors[field.id] = error
					isValid = false
				}
			}
		})

		return { isValid, errors }
	}

	const validationResults = validateForm()

	const updateFormData = (fieldId: string, value: any) => {
		setFormData(prev => ({
			...prev,
			[fieldId]: value
		}))
	}

	const calculateProgress = () => {
		const totalFields = visibleFields.length
		const completedFields = visibleFields.filter(field => {
			const value = formData[field.id]
			return value !== undefined && value !== '' && value !== false
		}).length

		return totalFields > 0 ? (completedFields / totalFields) * 100 : 0
	}

	const formProgress = calculateProgress()

	const renderField = (field: FormField) => {
		const hasError = validationResults.errors[field.id]

		switch (field.type) {
			case 'text':
			case 'email':
				return (
					<Field
						key={field.id}
						label={field.label}
						required={field.required}
						validationMessage={hasError}
						validationState={hasError ? 'error' : 'none'}
					>
						<Input
							contentBefore={field.type === 'email' ? <MailRegular /> : <PersonRegular />}
							type={field.type}
							value={field.value}
							onChange={(_, data) => updateFormData(field.id, data.value)}
						/>
					</Field>
				)

			case 'dropdown':
				return (
					<Field
						key={field.id}
						label={field.label}
						required={field.required}
						validationMessage={hasError}
						validationState={hasError ? 'error' : 'none'}
					>
						<Dropdown
							placeholder={`Select ${field.label.toLowerCase()}`}
							value={field.value}
							onOptionSelect={(_, data) => updateFormData(field.id, data.optionValue)}
						>
							{field.options?.map(option => (
								<Option key={option} value={option}>{option}</Option>
							))}
						</Dropdown>
					</Field>
				)

			case 'textarea':
				return (
					<Field
						key={field.id}
						label={field.label}
						required={field.required}
						validationMessage={hasError}
						validationState={hasError ? 'error' : 'none'}
					>
						<Textarea
							rows={4}
							value={field.value}
							onChange={(_, data) => updateFormData(field.id, data.value)}
						/>
					</Field>
				)

			case 'radio':
				return (
					<Field
						key={field.id}
						label={field.label}
						required={field.required}
						validationMessage={hasError}
						validationState={hasError ? 'error' : 'none'}
					>
						<RadioGroup
							value={field.value}
							onChange={(_, data) => updateFormData(field.id, data.value)}
						>
							{field.options?.map(option => (
								<Radio key={option} value={option} label={option} />
							))}
						</RadioGroup>
					</Field>
				)

			case 'checkbox':
				return (
					<Field
						key={field.id}
						validationMessage={hasError}
						validationState={hasError ? 'error' : 'none'}
					>
						<Checkbox
							checked={field.value}
							onChange={(_, data) => updateFormData(field.id, data.checked)}
							label={field.label}
						/>
					</Field>
				)

			default:
				return null
		}
	}

	const handleSubmit = async () => {
		if (!validationResults.isValid) {
			setSubmitMessage({
				type: 'error',
				message: 'Please fix the validation errors before submitting.'
			})
			return
		}

		setIsSubmitting(true)
		setSubmitMessage(null)

		// Simulate API call
		try {
			await new Promise(resolve => setTimeout(resolve, 2000))
			setSubmitMessage({
				type: 'success',
				message: 'Form submitted successfully! Your request has been processed.'
			})
			// Reset form
			setFormData({})
		} catch (error) {
			setSubmitMessage({
				type: 'error',
				message: `Failed to submit form. Please try again. ${error instanceof Error ? error.message : String(error)}`
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	const getUrgencyBadge = (urgency: string) => {
		const badgeProps = {
			'Low': { appearance: 'filled' as const, color: 'success' as const },
			'Medium': { appearance: 'filled' as const, color: 'warning' as const },
			'High': { appearance: 'filled' as const, color: 'danger' as const },
			'Critical': { appearance: 'filled' as const, color: 'severe' as const }
		}
		return urgency ? <Badge {...badgeProps[urgency as keyof typeof badgeProps]}>{urgency}</Badge> : null
	}

	return (
		<div className={styles.container}>
			<Card className={styles.header}>
				<CardHeader
					header={
						<Text size={600} weight="semibold">
							Dynamic SPFx Form Component
						</Text>
					}
					description="Interactive form with conditional fields, real-time validation, and dynamic updates based on user input"
				/>
			</Card>

			{/* Progress Bar */}
			<div className={styles.progressContainer}>
				<Text size={300} weight="semibold">Form Completion: {Math.round(formProgress)}%</Text>
				<ProgressBar value={formProgress / 100} />
			</div>

			{/* Form Sections */}
			<Card className={styles.formSection}>
				<Text size={500} weight="semibold" style={{ marginBottom: '1rem' }}>
					Personal Information
				</Text>
				<div className={styles.fieldGroup}>
					{visibleFields.slice(0, 4).map(renderField)}
				</div>
			</Card>

			<Card className={styles.formSection}>
				<Text size={500} weight="semibold" style={{ marginBottom: '1rem' }}>
					Request Details {formData.urgency && getUrgencyBadge(formData.urgency)}
				</Text>
				<div className={styles.fieldGroup}>
					{visibleFields.slice(4, -2).map(renderField)}
				</div>
			</Card>

			<Card className={styles.formSection}>
				<Text size={500} weight="semibold" style={{ marginBottom: '1rem' }}>
					Additional Information
				</Text>
				<div className={styles.fieldGroup}>
					{visibleFields.slice(-2).map(renderField)}
				</div>
			</Card>

			{/* Submit Section */}
			<div className={styles.submitSection}>
				<Button
					appearance="primary"
					size="large"
					disabled={!validationResults.isValid || isSubmitting}
					onClick={handleSubmit}
					icon={isSubmitting ? undefined : <CheckmarkCircleRegular />}
				>
					{isSubmitting ? 'Submitting...' : 'Submit Request'}
				</Button>

				<Button appearance="secondary" size="large" onClick={() => setFormData({})}>
					Reset Form
				</Button>

				<Text size={200}>
					Fields completed: {visibleFields.filter(f => formData[f.id] !== undefined && formData[f.id] !== '' && formData[f.id] !== false).length} / {visibleFields.length}
				</Text>
			</div>

			{/* Submit Message */}
			{submitMessage && (
				<MessageBar intent={submitMessage.type}>
					<MessageBarBody>
						<MessageBarTitle>
							{submitMessage.type === 'success' ? 'Success!' : 'Error'}
						</MessageBarTitle>
						{submitMessage.message}
					</MessageBarBody>
				</MessageBar>
			)}

			<Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '1rem' }}>
				* This demo showcases SPFx form capabilities including conditional fields, real-time validation,
				and dynamic UI updates. In production, this would integrate with SharePoint lists, other data sources & apps,
				Power Automate workflows, and approval processes.
			</Text>
		</div>
	)
}

export default DynamicForm